import { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/shared/hooks/useAuth";
import { supabase } from "@/shared/integrations/supabase/client";
import { useInstitutionAccess } from "@/shared/hooks/useInstitutionAccess";
import { Loader2 } from "lucide-react";

type PerfilInternoGate = { qualificacao: string | null; status: string | null };

/** UUID comum em Postgres (qualquer versão / variante hex). */
const UUID_HEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Slug legado em PT (`instituicoes`) → canônico `institutions` (mesmas rotas). */
function normalizeDashboardInstitutionsPath(pathname: string): string {
  return pathname.replace(/^\/dashboard\/instituicoes(?=\/|$)/, "/dashboard/institutions");
}

/**
 * Extrai o UUID da instituição em rotas do dashboard que o portal externo
 * também usa via `InstitutionRouteGuard` (detalhe, edição, documentos).
 */
function parseDashboardInstitutionIdFromPath(pathname: string): string | null {
  const pathnameNorm = normalizeDashboardInstitutionsPath(pathname);
  const prefix = "/dashboard/institutions/";
  if (!pathnameNorm.startsWith(prefix)) return null;
  const rest = pathnameNorm.slice(prefix.length);
  const segments = rest.split("/").filter(Boolean);
  if (segments.length === 0) return null;
  if (segments[0] === "new") return null;
  if (segments[0] === "edit" && segments[1] && UUID_HEX.test(segments[1])) return segments[1];
  if (UUID_HEX.test(segments[0])) {
    if (segments.length === 1) return segments[0];
    if (segments.length === 2 && segments[1] === "documents") return segments[0];
  }
  return null;
}

/**
 * Blocks users with qualificacao='cliente' from accessing internal routes.
 * Users with qualificacao='institution_external' are redirected to /acesso-externo/portal,
 * exceto nas rotas da própria instituição em `/dashboard/institutions/...` (ou slug legado
 * `instituicoes`) — alinhado ao
 * `InstitutionRouteGuard` — senão o utilizador nunca chega ao detalhe).
 */
const InternalGuard = ({ children }: { children: React.ReactNode }) => {
  const { user, loading: authLoading } = useAuth();
  const location = useLocation();
  const { institutionId, loading: accessLoading } = useInstitutionAccess();
  const [perfil, setPerfil] = useState<PerfilInternoGate | null>(null);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    if (!user) {
      setPerfil(null);
      setChecked(true);
      return;
    }

    const timeoutId = setTimeout(() => setChecked(true), 5000);

    supabase
      .from("profiles")
      .select("qualificacao, status")
      .eq("user_id", user.id)
      .maybeSingle()
      .then(({ data }) => {
        setPerfil({
          qualificacao: data?.qualificacao ?? null,
          status: data?.status ?? null,
        });
        setChecked(true);
      })
      .catch(() => setChecked(true))
      .finally(() => clearTimeout(timeoutId));

    return () => clearTimeout(timeoutId);
  }, [user?.id]);

  if (authLoading || !checked) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const qual = perfil?.qualificacao ?? null;
  const status = perfil?.status ?? null;

  if (qual === "cliente") {
    return <Navigate to="/acesso-externo/portal" replace />;
  }

  if (qual === "institution_external") {
    if (status === "inativo") {
      return <Navigate to="/" replace />;
    }
    if (accessLoading) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      );
    }
    const pathInstitutionId = parseDashboardInstitutionIdFromPath(location.pathname);
    const isOwnInstitutionDashboard =
      institutionId != null && pathInstitutionId != null && pathInstitutionId === institutionId;
    if (isOwnInstitutionDashboard) {
      return <>{children}</>;
    }
    if (!location.pathname.startsWith("/acesso-externo")) {
      return <Navigate to="/acesso-externo/portal" replace />;
    }
  }

  return <>{children}</>;
};

export default InternalGuard;
